import * as z from 'zod';

export const defaultExperimentSet = 'control';

export const experimentCookieKey = 'ci_exp_uid';
export const experimentAssignmentCookieKey = 'ci_exp_assignments';
export const experimentCacheCookieKey = 'ci_exp_cache';
export const experimentAssignmentVersion = 1;

/**
 * Contract shared by the blocking assignment route and the CloudFront
 * viewer-request function that redirects to it.
 *
 * These live here, not in `experiment-edge.ts`, because that module imports
 * `esbuild` for deploy-time serialization. Importing it from a route would pull
 * a Node-only native package into every app's bundle.
 */
export const experimentAssignmentPath = '/api/experiments/assign';
export const experimentAssignmentReturnPathParameter = '__sola_exp_return';

// Add new flags here before using them in Optimizely. The exhaustive
// experimentPathScopes map below requires every flag to declare where it can
// affect rendered output.
export const KnownExperiments = z.enum([
  'adhoc1',
  'adhoc2',
  'adhoc3',
  'adhoc4',
  'adhoc5',
  'checkoutChildImageSize',
  'checkoutProgressiveForm',
  'checkoutRecommendedBadges',
  'checkoutReviewStickySubmit',
  'checkoutSideSwap',
  'homeChildReel',
  'homeCurrentSponsorCards',
  'homeHero',
  'introRate',
  'homeSacFilterHero',
  'needBadges',
  'sacAdvancedFilters',
  'sacHero',
  'sacSortLongestWaiting',
  'sacTabbedInfo',
  'sublinks',
]);
export type KnownExperiment = z.infer<typeof KnownExperiments>;

export const ExperimentApplication = z.enum([
  'checkout',
  'manage',
  'my',
  'skippy-serve',
]);
export type ExperimentApplication = z.infer<typeof ExperimentApplication>;

type ExperimentPathTarget = {
  application: ExperimentApplication;
  pathPatterns: readonly string[];
};

const allApplications: readonly ExperimentApplication[] =
  ExperimentApplication.options;
const allPaths = ['/**'] as const;
const allApplicationPaths = allApplications.map((application) => ({
  application,
  pathPatterns: allPaths,
}));
const checkoutPaths = [
  {
    application: 'checkout',
    pathPatterns: allPaths,
  },
] as const;
const skippyHomePaths = [
  {
    application: 'skippy-serve',
    pathPatterns: ['/'],
  },
] as const;
const skippySponsorshipPaths = [
  {
    application: 'skippy-serve',
    pathPatterns: ['/sponsor-a-child', '/sponsor-a-child/**'],
  },
] as const;

/**
 * Source-controlled routing contract for experiment-aware page caching.
 * `/**` matches every path; a pattern ending in `/**` matches that path and
 * its descendants. Keep multi-page experiments on every affected path so one
 * user assignment is applied consistently throughout the journey.
 */
export const experimentPathScopes = {
  adhoc1: allApplicationPaths,
  adhoc2: allApplicationPaths,
  adhoc3: allApplicationPaths,
  adhoc4: allApplicationPaths,
  adhoc5: allApplicationPaths,
  checkoutChildImageSize: checkoutPaths,
  checkoutProgressiveForm: checkoutPaths,
  checkoutRecommendedBadges: checkoutPaths,
  checkoutReviewStickySubmit: checkoutPaths,
  checkoutSideSwap: checkoutPaths,
  homeChildReel: skippyHomePaths,
  homeCurrentSponsorCards: skippyHomePaths,
  homeHero: skippyHomePaths,
  introRate: [...skippySponsorshipPaths, ...checkoutPaths],
  homeSacFilterHero: skippyHomePaths,
  needBadges: skippySponsorshipPaths,
  sacAdvancedFilters: skippySponsorshipPaths,
  sacHero: skippySponsorshipPaths,
  sacSortLongestWaiting: skippySponsorshipPaths,
  sacTabbedInfo: skippySponsorshipPaths,
  sublinks: [
    {
      application: 'skippy-serve',
      pathPatterns: allPaths,
    },
  ],
} satisfies Record<KnownExperiment, readonly ExperimentPathTarget[]>;

const segmentMarker = '_';

export const filterExperiments = (experiments: string[]): string[] => {
  const selected = new Set(
    experiments.filter(
      (experiment) => KnownExperiments.safeParse(experiment).success,
    ),
  );
  const canonical = KnownExperiments.options.filter((experiment) =>
    selected.has(experiment),
  );
  return canonical.length ? canonical : [defaultExperimentSet];
};

export const encodeExperiments = (experiments: string[]): string =>
  filterExperiments(experiments).join(segmentMarker);

export const decodeExperiments = (encodedExperiments: string): string[] =>
  filterExperiments(encodedExperiments.split(segmentMarker));

export const isCanonicalExperimentSet = (encodedExperiments: string): boolean =>
  encodeExperiments(decodeExperiments(encodedExperiments)) ===
  encodedExperiments;

export const experimentsToClassnames = (experiments: string[]): string =>
  filterExperiments(experiments)
    .map((experiment) => `experiment-${experiment}`)
    .join(' ');

const pathMatchesPattern = (pathname: string, pattern: string): boolean => {
  if (pattern === '/**') {
    return true;
  }
  if (!pattern.endsWith('/**')) {
    return pathname === pattern;
  }
  const prefix = pattern.slice(0, -3);
  return pathname === prefix || pathname.startsWith(`${prefix}/`);
};

export const getApplicableExperiments = (
  application: ExperimentApplication,
  pathname: string,
): KnownExperiment[] =>
  KnownExperiments.options.filter((experiment) =>
    experimentPathScopes[experiment].some(
      (target) =>
        target.application === application &&
        target.pathPatterns.some((pattern) =>
          pathMatchesPattern(pathname, pattern),
        ),
    ),
  );

export type ExperimentAssignmentDecision = {
  /** Stable Optimizely experiment/rule identity used to avoid rebucketing. */
  experimentId: string | null;
  /** Whether the `on` variation is assigned. */
  isEnabled: boolean;
};

export type ExperimentAssignment = {
  version: typeof experimentAssignmentVersion;
  decisions: Partial<Record<KnownExperiment, ExperimentAssignmentDecision>>;
};

/**
 * Stored (wire) form of an assignment, using deliberately short keys.
 *
 * This shape only ever appears inside the `ci_exp_assignments` cookie, which
 * is sent on every request and counts against the CloudFront request header
 * budget. With all 21 experiments assigned, short keys cost ~1.6 KB
 * URL-encoded versus ~2.1 KB for readable keys.
 *
 * Application code never sees this shape. `serializeExperimentAssignment` and
 * `readExperimentAssignmentFromCookieValue` translate at the boundary, so the
 * rest of the codebase uses the readable `ExperimentAssignment` type.
 *
 *   v -> version
 *   a -> decisions
 *   i -> experimentId
 *   o -> isEnabled
 *
 * The CloudFront viewer-request function in `experiment-edge-runtime.ts` also
 * reads these keys directly, because it parses the cookie itself.
 */
const StoredDecisionSchema = z.object({
  i: z.string().max(128).nullable(),
  o: z.boolean(),
});

const StoredAssignmentSchema = z.object({
  v: z.literal(experimentAssignmentVersion),
  a: z.record(z.string(), StoredDecisionSchema),
});

/**
 * Rebuilds the decision map in `KnownExperiments` order and drops unknown
 * flags, so two equivalent assignments always serialize identically.
 */
const normalizeExperimentAssignment = (
  assignment: ExperimentAssignment,
): ExperimentAssignment => {
  const decisions: ExperimentAssignment['decisions'] = {};
  for (const experiment of KnownExperiments.options) {
    const decision = assignment.decisions[experiment];
    if (decision) {
      decisions[experiment] = decision;
    }
  }
  return {
    version: experimentAssignmentVersion,
    decisions,
  };
};

export const createEmptyExperimentAssignment = (): ExperimentAssignment => ({
  version: experimentAssignmentVersion,
  decisions: {},
});

/** Converts the readable assignment into the compact cookie payload. */
export const serializeExperimentAssignment = (
  assignment: ExperimentAssignment,
): string => {
  const normalized = normalizeExperimentAssignment(assignment);
  const storedDecisions: Record<string, { i: string | null; o: boolean }> = {};
  for (const [experiment, decision] of Object.entries(normalized.decisions)) {
    storedDecisions[experiment] = {
      i: decision.experimentId,
      o: decision.isEnabled,
    };
  }
  return JSON.stringify({
    v: normalized.version,
    a: storedDecisions,
  });
};

/** Parses the compact cookie payload back into the readable assignment. */
export const readExperimentAssignmentFromCookieValue = (
  rawValue: string | undefined,
): ExperimentAssignment | undefined => {
  if (!rawValue) {
    return undefined;
  }

  const candidates = [rawValue];
  try {
    candidates.push(decodeURIComponent(rawValue));
  } catch {
    // The raw value may already be decoded by Next.js.
  }

  for (const candidate of candidates) {
    try {
      const parsed = StoredAssignmentSchema.safeParse(JSON.parse(candidate));
      if (!parsed.success) {
        continue;
      }

      const decisions: ExperimentAssignment['decisions'] = {};
      for (const [key, storedDecision] of Object.entries(parsed.data.a)) {
        const experiment = KnownExperiments.safeParse(key);
        if (experiment.success) {
          decisions[experiment.data] = {
            experimentId: storedDecision.i,
            isEnabled: storedDecision.o,
          };
        }
      }
      return normalizeExperimentAssignment({
        version: experimentAssignmentVersion,
        decisions,
      });
    } catch {
      // Try the decoded candidate before treating the cookie as invalid.
    }
  }
  return undefined;
};

export const hasAssignmentsForExperiments = (
  assignment: ExperimentAssignment | undefined,
  experiments: readonly KnownExperiment[],
): boolean =>
  !!assignment &&
  experiments.every(
    (experiment) => assignment.decisions[experiment] !== undefined,
  );

export const getEncodedExperimentsForPath = (
  assignment: ExperimentAssignment | undefined,
  application: ExperimentApplication,
  pathname: string,
): string => {
  const enabled = getApplicableExperiments(application, pathname).filter(
    (experiment) => assignment?.decisions[experiment]?.isEnabled,
  );
  return encodeExperiments(enabled);
};

export const mergeExperimentAssignments = (
  current: ExperimentAssignment | undefined,
  evaluated: Partial<Record<KnownExperiment, ExperimentAssignmentDecision>>,
  applicableExperiments: readonly KnownExperiment[],
): ExperimentAssignment => {
  const merged = normalizeExperimentAssignment(
    current ?? createEmptyExperimentAssignment(),
  );

  for (const experiment of applicableExperiments) {
    const evaluatedDecision = evaluated[experiment] ?? {
      experimentId: null,
      isEnabled: false,
    };
    const currentDecision = merged.decisions[experiment];

    // Keep a visitor sticky when Optimizely changes allocation or audience
    // rules without replacing the underlying experiment/rule identity.
    merged.decisions[experiment] =
      currentDecision?.experimentId &&
      currentDecision.experimentId === evaluatedDecision.experimentId
        ? currentDecision
        : evaluatedDecision;
  }

  return normalizeExperimentAssignment(merged);
};

/** Preserve known sticky decisions during an outage and default only gaps. */
export const fillMissingExperimentAssignmentsWithControl = (
  current: ExperimentAssignment | undefined,
  applicableExperiments: readonly KnownExperiment[],
): ExperimentAssignment => {
  const fallback = normalizeExperimentAssignment(
    current ?? createEmptyExperimentAssignment(),
  );
  for (const experiment of applicableExperiments) {
    fallback.decisions[experiment] ??= {
      experimentId: null,
      isEnabled: false,
    };
  }
  return fallback;
};

export const OptimizelyUserAttributes = z.object({
  pathname: z.string().optional().nullable(),
  hostname: z.string().optional().nullable(),
  utmSource: z.string().optional().nullable(),
  utmMedium: z.string().optional().nullable(),
  utmCampaign: z.string().optional().nullable(),
  utmContent: z.string().optional().nullable(),
  rscReferrer: z.string().optional().nullable(),
  rscSourceCode: z.string().optional().nullable(),
});
export type OptimizelyUserAttributes = z.infer<typeof OptimizelyUserAttributes>;

export const OptimizelyResults = z
  .object({
    campaignId: z.string().optional().nullable(),
    enabled: z.boolean().optional(),
    experimentId: z.string().optional().nullable(),
    flagKey: z.string().optional().nullable(),
    reasons: z.string().array().optional(),
    ruleKey: z.string().optional().nullable(),
    variationId: z.string().optional().nullable(),
    variationKey: z.string().optional().nullable(),
  })
  .array();
export type OptimizelyResults = z.infer<typeof OptimizelyResults>;

export const OptimizelyCookieSchema = z.object({
  uid: z.string().optional(),
});

/** Canonical stored form of the visitor cookie: `{"uid":"..."}`. */
export const serializeExperimentCookie = (uid: string): string =>
  JSON.stringify({ uid });

/**
 * Reads the visitor ID out of a raw cookie value.
 *
 * Accepts both shapes we may encounter:
 *   1. `{"uid":"abc123"}` — what current code writes.
 *   2. `abc123` — a bare string written by older code. `JSON.parse` throws on
 *      these, so we reuse the raw value instead of discarding a valid visitor.
 *
 * Returns `undefined` when there is no usable ID.
 */
export const readUidFromCookieValue = (
  rawValue?: string,
): string | undefined => {
  if (!rawValue) {
    return undefined;
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(rawValue);
  } catch {
    return rawValue;
  }

  const cookie = OptimizelyCookieSchema.safeParse(parsed);
  return cookie.success && cookie.data.uid ? cookie.data.uid : undefined;
};
